import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Parameter, PolygonAxisConstraint } from '@shared/types';
import { Input } from '@/components/ui/input';
import { getParameterIcon } from '@/utils/parameterUtils';
import { ParameterLabel } from '@/components/parameter/ParameterLabel';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function snapToStep(value: number, step: number | undefined): number {
  if (!step || step <= 0) return value;
  return Math.round(value / step) * step;
}

/** Apply a constraint to a candidate value: clamp + snap for range, lock for fixed. */
function applyConstraint(
  value: number,
  constraint: PolygonAxisConstraint,
): number {
  if (constraint.kind === 'fixed') return constraint.value;
  if (constraint.kind === 'range') {
    return snapToStep(
      clamp(value, constraint.min, constraint.max),
      constraint.step,
    );
  }
  // free — no clamping
  return value;
}

/** True when the constraint allows user editing. */
function isEditable(constraint: PolygonAxisConstraint): boolean {
  return constraint.kind !== 'fixed';
}

interface CanvasBounds {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

/**
 * Compute canvas coordinate bounds that fit all polygon points and any
 * explicit constraint ranges, with ~15% padding on each side.
 */
function computeCanvasBounds(
  points: [number, number][],
  constraints: Array<[PolygonAxisConstraint, PolygonAxisConstraint]>,
): CanvasBounds {
  const allX: number[] = [];
  const allY: number[] = [];

  points.forEach(([x, y]) => {
    allX.push(x);
    allY.push(y);
  });

  // Also include explicit range bounds so the canvas shows the full movable area
  constraints.forEach(([xc, yc]) => {
    if (xc.kind === 'range') {
      allX.push(xc.min, xc.max);
    }
    if (yc.kind === 'range') {
      allY.push(yc.min, yc.max);
    }
  });

  const rawXMin = Math.min(...allX);
  const rawXMax = Math.max(...allX);
  const rawYMin = Math.min(...allY);
  const rawYMax = Math.max(...allY);

  const xSpan = Math.max(rawXMax - rawXMin, 1);
  const ySpan = Math.max(rawYMax - rawYMin, 1);
  const xPad = xSpan * 0.15;
  const yPad = ySpan * 0.15;

  return {
    xMin: rawXMin - xPad,
    xMax: rawXMax + xPad,
    yMin: rawYMin - yPad,
    yMax: rawYMax + yPad,
  };
}

function valueToFrac(value: number, min: number, max: number): number {
  if (max === min) return 0;
  return clamp((value - min) / (max - min), 0, 1);
}

function fracToValue(frac: number, min: number, max: number): number {
  return min + clamp(frac, 0, 1) * (max - min);
}

/** Format a number for display — strip unnecessary trailing zeros. */
function fmt(n: number): string {
  return String(parseFloat(n.toPrecision(7)));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PAD_HEIGHT_PX = 200; // fixed SVG logical height in px

export function PolygonPadInput({
  param,
  onCommit,
}: {
  param: Parameter;
  onCommit: (value: Parameter['value']) => void;
}) {
  const config = param.polygonPadConfig;
  const flat = Array.isArray(param.value) ? (param.value as number[]) : [];
  const nPoints = flat.length / 2;

  // Local flat state: [x0, y0, x1, y1, ...]
  const [localFlat, setLocalFlat] = useState<number[]>(flat);

  // Keep in sync when parent pushes a new value
  const prevValueRef = useRef(param.value);
  if (prevValueRef.current !== param.value) {
    prevValueRef.current = param.value;
    setLocalFlat(flat);
  }

  // Derive per-point constraint pairs
  const constraints: Array<[PolygonAxisConstraint, PolygonAxisConstraint]> =
    config?.pointConstraints ??
    Array.from(
      { length: nPoints },
      (_, i) =>
        [
          { kind: 'free', defaultValue: flat[i * 2] },
          { kind: 'free', defaultValue: flat[i * 2 + 1] },
        ] as [PolygonAxisConstraint, PolygonAxisConstraint],
    );

  // Unpack points for canvas
  const points: [number, number][] = Array.from({ length: nPoints }, (_, i) => [
    localFlat[i * 2],
    localFlat[i * 2 + 1],
  ]);

  const bounds = computeCanvasBounds(points, constraints);
  const xSpan = Math.max(bounds.xMax - bounds.xMin, 1);
  const ySpan = Math.max(bounds.yMax - bounds.yMin, 1);

  // Aspect ratio capped to a sane range, consistent with XYPadInput
  const rawAspect = xSpan / ySpan;
  const aspect = Math.min(2.5, Math.max(0.4, rawAspect));

  // Throttled commit (200ms)
  const lastCommitTime = useRef(0);
  const commitTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const throttledCommit = useCallback(
    (newFlat: number[]) => {
      const now = Date.now();
      const doCommit = () => {
        onCommit(newFlat);
        lastCommitTime.current = Date.now();
        commitTimeout.current = null;
      };
      if (now - lastCommitTime.current >= 200) {
        doCommit();
      } else {
        if (commitTimeout.current) clearTimeout(commitTimeout.current);
        commitTimeout.current = setTimeout(
          doCommit,
          200 - (now - lastCommitTime.current),
        );
      }
    },
    [onCommit],
  );

  const immediateCommit = useCallback(
    (newFlat: number[]) => {
      if (commitTimeout.current) {
        clearTimeout(commitTimeout.current);
        commitTimeout.current = null;
      }
      onCommit(newFlat);
      lastCommitTime.current = Date.now();
    },
    [onCommit],
  );

  useEffect(() => {
    return () => {
      if (commitTimeout.current) clearTimeout(commitTimeout.current);
    };
  }, []);

  // Dragging state
  const svgRef = useRef<SVGSVGElement>(null);
  const draggingIndex = useRef<number>(-1);

  const pointerToCoords = useCallback(
    (clientX: number, clientY: number): [number, number] => {
      const el = svgRef.current;
      if (!el) return [0, 0];
      const rect = el.getBoundingClientRect();
      const fracX = (clientX - rect.left) / rect.width;
      // Screen Y inverted: top = yMax, bottom = yMin
      const fracY = 1 - (clientY - rect.top) / rect.height;
      return [
        fracToValue(fracX, bounds.xMin, bounds.xMax),
        fracToValue(fracY, bounds.yMin, bounds.yMax),
      ];
    },
    [bounds],
  );

  const handlePointPointerDown = (
    e: React.PointerEvent<SVGCircleElement>,
    idx: number,
  ) => {
    const xc = constraints[idx][0];
    const yc = constraints[idx][1];
    // Don't start drag if both axes are fixed
    if (!isEditable(xc) && !isEditable(yc)) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    draggingIndex.current = idx;
    e.stopPropagation();
  };

  const handleSvgPointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      const idx = draggingIndex.current;
      if (idx < 0) return;
      const [rawX, rawY] = pointerToCoords(e.clientX, e.clientY);
      const xc = constraints[idx][0];
      const yc = constraints[idx][1];
      const newX = applyConstraint(rawX, xc);
      const newY = applyConstraint(rawY, yc);
      setLocalFlat((prev) => {
        const next = [...prev];
        next[idx * 2] = newX;
        next[idx * 2 + 1] = newY;
        throttledCommit(next);
        return next;
      });
    },
    [constraints, pointerToCoords, throttledCommit],
  );

  const handleSvgPointerUp = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      const idx = draggingIndex.current;
      if (idx < 0) return;
      draggingIndex.current = -1;
      const [rawX, rawY] = pointerToCoords(e.clientX, e.clientY);
      const xc = constraints[idx][0];
      const yc = constraints[idx][1];
      const newX = applyConstraint(rawX, xc);
      const newY = applyConstraint(rawY, yc);
      setLocalFlat((prev) => {
        const next = [...prev];
        next[idx * 2] = newX;
        next[idx * 2 + 1] = newY;
        immediateCommit(next);
        return next;
      });
    },
    [constraints, pointerToCoords, immediateCommit],
  );

  // Input field handlers
  const handleInputChange = (pointIdx: number, axis: 0 | 1, raw: string) => {
    const n = parseFloat(raw);
    if (isNaN(n)) return;
    setLocalFlat((prev) => {
      const next = [...prev];
      next[pointIdx * 2 + axis] = n;
      return next;
    });
  };

  const handleInputBlur = (pointIdx: number, axis: 0 | 1) => {
    const constraint = constraints[pointIdx][axis];
    const raw = localFlat[pointIdx * 2 + axis];
    const clamped = applyConstraint(raw, constraint);
    setLocalFlat((prev) => {
      const next = [...prev];
      next[pointIdx * 2 + axis] = clamped;
      immediateCommit(next);
      return next;
    });
  };

  const handleInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };

  // SVG coordinate system: width × height in pixels, matching the aspect ratio.
  const svgW = Math.round(PAD_HEIGHT_PX * aspect);
  const svgH = PAD_HEIGHT_PX;

  // Map a world coordinate to SVG pixel coordinates.
  const toSvgX = (x: number) => valueToFrac(x, bounds.xMin, bounds.xMax) * svgW;
  const toSvgY = (y: number) =>
    (1 - valueToFrac(y, bounds.yMin, bounds.yMax)) * svgH;

  // Build polyline points string in pixel coordinates
  const polylinePoints = points
    .map(([x, y]) => `${toSvgX(x)},${toSvgY(y)}`)
    .join(' ');

  // Closing line back to first point
  const closingLine =
    points.length >= 2
      ? `${polylinePoints} ${polylinePoints.split(' ')[0]}`
      : polylinePoints;

  const IconComponent = getParameterIcon(param.name);

  return (
    <div className="grid w-full grid-cols-[96px_1fr] items-start gap-3">
      <ParameterLabel
        param={param}
        IconComponent={IconComponent}
        className="pt-2"
      />
      <div className="flex flex-col gap-2">
        {/* SVG canvas */}
        <svg
          ref={svgRef}
          className="w-full cursor-crosshair touch-none select-none rounded-md border border-border bg-muted/40"
          style={{ height: PAD_HEIGHT_PX, aspectRatio: String(aspect) }}
          viewBox={`0 0 ${svgW} ${svgH}`}
          preserveAspectRatio="xMidYMid meet"
          onPointerMove={handleSvgPointerMove}
          onPointerUp={handleSvgPointerUp}
          onPointerCancel={handleSvgPointerUp}
        >
          {/* Grid lines (25% increments) */}
          {[0.25, 0.5, 0.75].map((frac) => (
            <React.Fragment key={frac}>
              <line
                x1={svgW * frac}
                y1="0"
                x2={svgW * frac}
                y2={svgH}
                stroke="currentColor"
                strokeOpacity="0.1"
                strokeWidth="1"
              />
              <line
                x1="0"
                y1={svgH * frac}
                x2={svgW}
                y2={svgH * frac}
                stroke="currentColor"
                strokeOpacity="0.1"
                strokeWidth="1"
              />
            </React.Fragment>
          ))}
          {/* Centre crosshair */}
          <line
            x1={svgW * 0.5}
            y1="0"
            x2={svgW * 0.5}
            y2={svgH}
            stroke="currentColor"
            strokeOpacity="0.18"
            strokeWidth="1"
            strokeDasharray="4 4"
          />
          <line
            x1="0"
            y1={svgH * 0.5}
            x2={svgW}
            y2={svgH * 0.5}
            stroke="currentColor"
            strokeOpacity="0.18"
            strokeWidth="1"
            strokeDasharray="4 4"
          />

          {/* Polygon outline (closed) */}
          {points.length >= 2 && (
            <polyline
              points={closingLine}
              fill="none"
              stroke="currentColor"
              strokeOpacity="0.35"
              strokeWidth="1.5"
            />
          )}

          {/* Point handles */}
          {points.map(([x, y], idx) => {
            const xc = constraints[idx][0];
            const yc = constraints[idx][1];
            const bothFixed = !isEditable(xc) && !isEditable(yc);
            const partiallyFixed =
              !bothFixed && (!isEditable(xc) || !isEditable(yc));
            const cx = toSvgX(x);
            const cy = toSvgY(y);

            return (
              <g key={idx}>
                {/* Invisible larger hit area for easier grabbing */}
                {!bothFixed && (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={6}
                    fill="transparent"
                    style={{ cursor: 'grab' }}
                    onPointerDown={(e) => handlePointPointerDown(e, idx)}
                  />
                )}
                {/* Visible handle */}
                <circle
                  cx={cx}
                  cy={cy}
                  r={bothFixed ? 2.5 : 4}
                  fill={
                    bothFixed
                      ? 'hsl(var(--muted-foreground))'
                      : partiallyFixed
                        ? 'hsl(var(--primary) / 0.6)'
                        : 'hsl(var(--primary))'
                  }
                  stroke="hsl(var(--background))"
                  strokeWidth={bothFixed ? 0 : 1.5}
                  style={{
                    cursor: bothFixed ? 'default' : 'grab',
                    pointerEvents: bothFixed ? 'none' : 'none',
                  }}
                />
                {/* Point index label */}
                <text
                  x={cx + 7}
                  y={cy - 5}
                  fontSize="10"
                  fill="currentColor"
                  fillOpacity="0.5"
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  {idx}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Per-point numeric inputs */}
        <div className="flex flex-col gap-1.5">
          {points.map(([x, y], idx) => {
            const xc = constraints[idx][0];
            const yc = constraints[idx][1];
            return (
              <div key={idx} className="flex items-center gap-2">
                <span className="w-6 shrink-0 text-center text-[10px] font-medium text-muted-foreground">
                  P{idx}
                </span>
                <div className="flex flex-1 gap-1.5">
                  {/* X input */}
                  <div className="flex flex-1 flex-col gap-0.5">
                    <span className="pl-1 text-[9px] text-muted-foreground">
                      X{!isEditable(xc) ? ' 🔒' : ''}
                    </span>
                    <Input
                      className="h-6 px-1.5 text-center text-xs"
                      value={fmt(localFlat[idx * 2] ?? x)}
                      disabled={!isEditable(xc)}
                      onChange={(e) =>
                        handleInputChange(idx, 0, e.target.value)
                      }
                      onFocus={(e) => e.target.select()}
                      onBlur={() => handleInputBlur(idx, 0)}
                      onKeyDown={handleInputKey}
                    />
                  </div>
                  {/* Y input */}
                  <div className="flex flex-1 flex-col gap-0.5">
                    <span className="pl-1 text-[9px] text-muted-foreground">
                      Y{!isEditable(yc) ? ' 🔒' : ''}
                    </span>
                    <Input
                      className="h-6 px-1.5 text-center text-xs"
                      value={fmt(localFlat[idx * 2 + 1] ?? y)}
                      disabled={!isEditable(yc)}
                      onChange={(e) =>
                        handleInputChange(idx, 1, e.target.value)
                      }
                      onFocus={(e) => e.target.select()}
                      onBlur={() => handleInputBlur(idx, 1)}
                      onKeyDown={handleInputKey}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
